import { useState, useEffect, useRef } from "react";
import { Play, Square } from "lucide-react";
import { useNoteGenerator } from "~/features/note-generator/useNoteGenerator";
import { useNotesStore } from "~/stores/useNotesStore";
import { useMusicStore } from "~/stores/useMusicStore";
import Button from "~/components/ui/Button";

// Calculate note duration in ms based on BPM and note length
function getNoteDurationMs(length: string, bpm: number): number {
  const beatDuration = 60000 / bpm; // quarter-note in ms
  switch (length) {
    case "1n": return beatDuration * 4;   // whole
    case "2n": return beatDuration * 2;   // half
    case "4n": return beatDuration;       // quarter
    case "8n": return beatDuration / 2;   // eighth
    case "16n": return beatDuration / 4;  // sixteenth
    default:   return beatDuration;
  }
}

export default function PlaybackControls() {
  const noteGenerator = useNoteGenerator();
  const start = useNotesStore((state) => state.start);
  const toggleStart = useNotesStore((state) => state.toggleStart);
  const bpm = useNotesStore((state) => state.bpm);
  const noteLength = useNotesStore((state) => state.noteLength);

  const synthReady = useMusicStore((state) => state.synthReady);
  const midiClockEnabled = useMusicStore((state) => state.midiClockEnabled);
  const midiClockRunning = useMusicStore((state) => state.midiClockRunning);
  const setMidiClockEnabled = useMusicStore((state) => state.setMidiClockEnabled);

  const [isMidiClockFlashing, setIsMidiClockFlashing] = useState(false);
  const midiClockFlashTimeoutRef = useRef<number | undefined>(undefined);

  // Trigger MIDI clock flash animation whenever a note is played
  useEffect(() => {
    const trigger = noteGenerator.noteTrigger; // This changes on every note play
    const note = noteGenerator.currentNote;
    const isPlaying = noteGenerator.isPlaying;

    if (note && trigger > 0 && isPlaying) {
      // Calculate flash duration as 20% of note duration (looks good at all BPMs)
      const noteDuration = getNoteDurationMs(noteLength.length, bpm);
      const flashDuration = Math.max(50, Math.min(200, noteDuration * 0.2)); // 50-200ms range

      // Flash MIDI clock button if it's enabled and running
      if (midiClockEnabled && midiClockRunning) {
        console.log('[MIDI CLOCK FLASH] Triggering flash');
        setIsMidiClockFlashing(true);

        // Clear any existing MIDI clock flash timeout
        if (midiClockFlashTimeoutRef.current) clearTimeout(midiClockFlashTimeoutRef.current);

        // Reset MIDI clock flash after duration
        midiClockFlashTimeoutRef.current = setTimeout(() => {
          console.log('[MIDI CLOCK FLASH] Ending flash');
          setIsMidiClockFlashing(false);
        }, flashDuration) as unknown as number;
      }
    }
  }, [noteGenerator.noteTrigger, noteGenerator.currentNote, noteGenerator.isPlaying, noteLength.length, bpm, midiClockEnabled, midiClockRunning]);

  // Debug: Log MIDI clock state changes
  useEffect(() => {
    console.log('[MIDI CLOCK] Enabled:', midiClockEnabled, 'Running:', midiClockRunning, 'Flashing:', isMidiClockFlashing);
  }, [midiClockEnabled, midiClockRunning, isMidiClockFlashing]);

  const renderPlayStopIcon = () => {
    if (start && !synthReady) {
      // Loading spinner
      return (
        <svg className="w-8 h-8 md:w-10 md:h-10 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      );
    }

    if (start) {
      // Stop icon
      return <Square className="w-8 h-8 md:w-10 md:h-10" />;
    }

    // Play icon
    return <Play className="w-8 h-8 md:w-10 md:h-10" />;
  };

  return (
    <div className="flex items-center gap-2 md:gap-3">
      {/* Play/Stop Button - Large, full footer height */}
      <Button
        variant="icon"
        onClick={toggleStart}
        disabled={start && !synthReady}
        className={`h-16 w-16 md:h-20 md:w-20 bg-gray-950 border-2 border-[var(--color-yomidi-neon-blue)] ${
          start
            ? "text-[var(--color-yomidi-neon-pink)]"
            : "text-[var(--color-yomidi-neon-blue)]"
        }`}
        title={
          start && !synthReady
            ? "Initializing synth..."
            : start
              ? "Stop playback (resets to beginning)"
              : "Start playback"
        }
        aria-label={start ? "Stop" : "Play"}
      >
        {renderPlayStopIcon()}
      </Button>

      {/* MIDI Clock Toggle - Hidden for now */}
      {false && (
        <Button
          variant="icon"
          onClick={() => setMidiClockEnabled(!midiClockEnabled)}
          className={
            midiClockEnabled && isMidiClockFlashing
              ? "border-purple-500 bg-purple-700 text-purple-100"
              : midiClockEnabled && !isMidiClockFlashing
                ? "bg-purple-900 text-purple-300"
                : "border-gray-700 hover:border-gray-500 bg-gray-900/50 text-gray-500 hover:text-gray-400"
          }
          style={{
            transitionDuration: isMidiClockFlashing
              ? "50ms"
              : `${Math.max(50, Math.min(200, getNoteDurationMs(noteLength.length, bpm) * 0.2))}ms`
          }}
          title={midiClockEnabled ? "MIDI Clock enabled (syncs external hardware)" : "Enable MIDI Clock (for eurorack/hardware sync)"}
          aria-label={midiClockEnabled ? "Disable MIDI Clock" : "Enable MIDI Clock"}
        >
          <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <polyline points="12 6 12 12 16 14"></polyline>
          </svg>
        </Button>
      )}
    </div>
  );
}
